import { useId, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Download, FileUp, TriangleAlert } from "lucide-react";

import Button from "../button/Button";
import { parseCsv, toCsv, type CsvTable } from "../../domain/csv";
import type { CsvColumn, CsvImport, CsvRowError } from "../../domain/csvImport";

/** What the API answered for one item: created, or the code it was refused with. */
export interface CsvOutcome {
    error: string | null;
    /** The centre's own field a refusal is about, when the API names one. */
    field_id?: string | null;
}

interface Props<T> {
    columns: CsvColumn[];
    read: (table: CsvTable) => CsvImport<T>;
    /** The downloaded template's file name, without extension. */
    templateName: string;
    /** Example rows for the template; one made of the columns' examples otherwise. */
    templateRows?: string[][];
    /** Anything the administrator needs beside the columns, such as the course ids. */
    hint?: ReactNode;
    /** Sends the items and answers, in the same order, what became of each. */
    onImport: (items: T[]) => Promise<CsvOutcome[]>;
    /** How each item is named in the results. */
    describe: (item: T) => string;
    /** The sentence for an API refusal code, given what the API answered for that item. */
    errorMessage: (code: string, outcome: CsvOutcome) => string;
    submitLabel: (count: number) => string;
    /** Said before importing, from how many items the file holds — e.g. more than there is room for. */
    warning?: (count: number) => string | null;
}

/** The file's text. FileReader rather than `File.text()`, which older browsers lack. */
function readText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ""));
        reader.onerror = () => reject(reader.error);
        reader.readAsText(file, "utf-8");
    });
}

/**
 * Importing a spreadsheet: what it must carry, a template, a preview, results.
 *
 * Nothing is sent until the administrator has seen what the file holds and
 * which rows it refuses by itself. What the API refuses afterwards — a mentor
 * who is not the centre's, an address already taken — comes back per row, so
 * the rows to fix are named rather than the whole file failing.
 */
export default function CsvImportPanel<T>({
    columns,
    read,
    templateName,
    templateRows,
    hint,
    onImport,
    describe,
    errorMessage,
    submitLabel,
    warning,
}: Props<T>) {
    const { t } = useTranslation();
    const inputId = useId();
    const [fileName, setFileName] = useState<string | null>(null);
    const [parsed, setParsed] = useState<CsvImport<T> | null>(null);
    const [unreadable, setUnreadable] = useState(false);
    const [results, setResults] = useState<{ item: T; lines: number[]; outcome: CsvOutcome }[] | null>(null);
    const [sending, setSending] = useState(false);
    const [failed, setFailed] = useState(false);

    const downloadTemplate = () => {
        const rows = templateRows ?? [columns.map((column) => column.example)];
        const blob = new Blob([toCsv(columns.map((column) => column.key), rows)], {
            type: "text/csv;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${templateName}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    const handleFile = async (file: File | undefined) => {
        setResults(null);
        setFailed(false);
        setParsed(null);
        setUnreadable(false);
        if (!file) return;
        setFileName(file.name);
        try {
            setParsed(read(parseCsv(await readText(file))));
        } catch {
            setUnreadable(true);
        }
    };

    const handleImport = async () => {
        if (!parsed || parsed.items.length === 0) return;
        setSending(true);
        setFailed(false);
        try {
            const outcomes = await onImport(parsed.items.map((item) => item.value));
            setResults(
                parsed.items.map((item, index) => ({
                    item: item.value,
                    lines: item.lines,
                    outcome: outcomes[index] ?? { error: null },
                }))
            );
        } catch {
            setFailed(true);
        } finally {
            setSending(false);
        }
    };

    const rowError = (error: CsvRowError) =>
        t(`center.csv.errors.${error.code}`, {
            line: error.line,
            column: error.column ?? "",
        });

    const created = results?.filter((r) => !r.outcome.error) ?? [];
    const refused = results?.filter((r) => r.outcome.error) ?? [];

    return (
        <div className="flex flex-col gap-5">
            <div>
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <h4 className="text-sm font-semibold text-content-primary">{t("center.csv.columnsTitle")}</h4>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={downloadTemplate}
                        startIcon={<Download size={14} aria-hidden="true" />}
                    >
                        {t("center.csv.downloadTemplate")}
                    </Button>
                </div>
                <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800">
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500 dark:border-gray-800 dark:text-gray-400">
                                <th scope="col" className="px-3 py-2 font-medium">{t("center.csv.column")}</th>
                                <th scope="col" className="px-3 py-2 font-medium">{t("center.csv.content")}</th>
                                <th scope="col" className="px-3 py-2 font-medium">{t("center.csv.requiredHeader")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {columns.map((column) => (
                                <tr key={column.key} className="border-b border-gray-100 last:border-0 dark:border-gray-800">
                                    <td className="px-3 py-2 font-mono text-xs text-content-primary">{column.key}</td>
                                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                                        {column.description ?? t(`center.csv.columns.${column.key}`)}
                                    </td>
                                    <td className="px-3 py-2">
                                        {column.requirement ? (
                                            <span className="rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700 dark:bg-brand-500/10 dark:text-brand-300">
                                                {column.requirement}
                                            </span>
                                        ) : column.required ? (
                                            <span className="rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700 dark:bg-brand-500/10 dark:text-brand-300">
                                                {t("center.csv.required")}
                                            </span>
                                        ) : (
                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                                {t("center.csv.optional")}
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{t("center.csv.formatHint")}</p>
                {hint && <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">{hint}</div>}
            </div>

            <div>
                <label
                    htmlFor={inputId}
                    className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 px-4 py-5 text-sm text-gray-600 hover:border-brand-400 hover:text-brand-600 focus-within:border-brand-500 dark:border-gray-700 dark:text-gray-400"
                >
                    <FileUp size={18} aria-hidden="true" />
                    {fileName ?? t("center.csv.chooseFile")}
                </label>
                <input
                    id={inputId}
                    type="file"
                    accept=".csv,text/csv"
                    className="sr-only"
                    aria-label={t("center.csv.chooseFile")}
                    onChange={(e) => void handleFile(e.target.files?.[0])}
                />
            </div>

            {unreadable && (
                <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                    {t("center.csv.unreadable")}
                </p>
            )}

            {parsed && !results && (
                <div aria-live="polite" className="flex flex-col gap-2 text-sm">
                    {parsed.missingColumns.length > 0 ? (
                        <p role="alert" className="text-red-600 dark:text-red-400">
                            {t("center.csv.missingColumns", { columns: parsed.missingColumns.join(", ") })}
                        </p>
                    ) : (
                        <>
                            <p className="text-content-primary">
                                {t("center.csv.ready", { count: parsed.items.length })}
                            </p>
                            {warning?.(parsed.items.length) && (
                                <p
                                    role="alert"
                                    className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300"
                                >
                                    {warning(parsed.items.length)}
                                </p>
                            )}
                            {parsed.errors.length > 0 && (
                                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300">
                                    <p className="mb-1 flex items-center gap-1.5 font-medium">
                                        <TriangleAlert size={14} aria-hidden="true" />
                                        {t("center.csv.skipped", { count: parsed.errors.length })}
                                    </p>
                                    <ul className="list-disc pl-5">
                                        {parsed.errors.map((error, index) => (
                                            <li key={index}>{rowError(error)}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {failed && (
                <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                    {t("center.csv.failed")}
                </p>
            )}

            {results && (
                <div aria-live="polite" className="flex flex-col gap-2 text-sm">
                    <p className="flex items-center gap-1.5 font-medium text-content-primary">
                        <CheckCircle2 size={16} className="text-brand-600 dark:text-brand-300" aria-hidden="true" />
                        {t("center.csv.done", { created: created.length, refused: refused.length })}
                    </p>
                    {refused.length > 0 && (
                        <ul className="list-disc rounded-xl border border-red-200 bg-red-50 py-3 pl-8 pr-3 text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
                            {refused.map((result, index) => (
                                <li key={index}>
                                    {t("center.csv.refusedRow", {
                                        lines: result.lines.join(", "),
                                        item: describe(result.item),
                                        reason: errorMessage(result.outcome.error as string, result.outcome),
                                    })}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            {!results && (
                <div className="flex justify-end">
                    <Button
                        onClick={() => void handleImport()}
                        disabled={!parsed || parsed.items.length === 0 || sending}
                        loading={sending}
                    >
                        {submitLabel(parsed?.items.length ?? 0)}
                    </Button>
                </div>
            )}
        </div>
    );
}
